/**
 * Widget Data Validators - STRICT MODE
 *
 * These validators THROW errors on invalid data to help detect:
 * 1. Endpoints returning incorrect data shapes
 * 2. Missing/broken endpoints (404s returning HTML)
 * 3. API contract violations
 *
 * In development, these errors are visible immediately.
 * In production, they're caught and logged for monitoring.
 */

import { RED } from '../../constants/chartColors';

export interface MetricData {
  value: number | string;
  label: string;
  format?: string;
  trend?: string;
  trendValue?: string | number;
  color?: string;
  displayValue?: string;
  link?: string;
  error?: boolean;
}

export interface ChartDataPoint {
  name?: string;
  value?: number;
  [key: string]: unknown;
}

export interface ChartData {
  chartType: string;
  data: ChartDataPoint[];
  xKey?: string;
  yKey?: string;
  yKeys?: string[];
  color?: string;
  colors?: string[];
  format?: string;
  stacked?: boolean;
  error?: boolean;
  link?: string;
}

export interface ListItem {
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  value?: string;
  timestamp?: string;
  link?: string;
  tooltip?: string;
  badge?: {
    text: string;
    variant?: string;
    color?: string;
    tooltip?: string;
  };
}

export interface ListData {
  items: ListItem[];
  maxItems?: number;
  showBadge?: boolean;
  error?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  data: unknown;
  errors?: string[];
}

/**
 * Custom error class for widget validation failures
 */
export class WidgetValidationError extends Error {
  widgetType: string;
  validationErrors: string[];
  receivedData: unknown;

  constructor(widgetType: string, errors: string[], receivedData: unknown) {
    super(`Widget validation failed for ${widgetType}: ${errors.join(', ')}`);
    this.name = 'WidgetValidationError';
    this.widgetType = widgetType;
    this.validationErrors = errors;
    this.receivedData = receivedData;
  }
}

/**
 * Validate metric widget data shape - STRICT
 */
export const validateMetricData = (data: unknown, _widgetId = 'unknown'): ValidationResult => {
  const errors: string[] = [];

  if (data == null) {
    throw new WidgetValidationError('metric', ['Data is null or undefined - endpoint may not exist'], data);
  }

  if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
    throw new WidgetValidationError('metric', ['Received HTML instead of JSON - endpoint likely returns 404'], data);
  }

  const d = data as Record<string, unknown>;

  if (typeof d.value !== 'number' && typeof d.value !== 'string') {
    errors.push(`value must be number or string, got ${typeof d.value}`);
  }

  if (d.format && !['number', 'currency', 'percentage', 'duration'].includes(d.format as string)) {
    errors.push(`Invalid format "${d.format}" - expected: number|currency|percentage|duration`);
  }

  if (d.trend && !['up', 'down', 'flat'].includes(d.trend as string)) {
    errors.push(`Invalid trend "${d.trend}" - expected: up|down|flat`);
  }

  if (!d.label || typeof d.label !== 'string') {
    errors.push('label is required and must be a string');
  }

  if (errors.length > 0) {
    throw new WidgetValidationError('metric', errors, data);
  }

  return { valid: true, data };
};

/**
 * Validate chart widget data shape - STRICT
 */
export const validateChartData = (data: unknown, widgetId = 'unknown'): ValidationResult => {
  const errors: string[] = [];

  if (data == null) {
    throw new WidgetValidationError('chart', ['Data is null or undefined - endpoint may not exist'], data);
  }

  if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
    throw new WidgetValidationError('chart', ['Received HTML instead of JSON - endpoint likely returns 404'], data);
  }

  const d = data as Record<string, unknown>;

  if (!d.chartType) {
    errors.push('chartType is required - expected: bar|line|area|pie|donut|horizontalBar');
  }

  const validChartTypes = ['bar', 'line', 'area', 'pie', 'donut', 'horizontalBar'];
  if (d.chartType && !validChartTypes.includes(d.chartType as string)) {
    errors.push(`Invalid chartType "${d.chartType}" - expected: ${validChartTypes.join('|')}`);
  }

  if (Array.isArray(d.data)) {
    if (d.data.length === 0) {
      console.warn(`[validateChartData] widget "${widgetId}": chart data array is empty`);
    }
  } else {
    errors.push(`data must be an array, got ${typeof d.data}`);
  }

  if (Array.isArray(d.data) && d.data.length > 0) {
    const firstItem = d.data[0] as Record<string, unknown>;
    if (['pie', 'donut'].includes(d.chartType as string)) {
      if (!('name' in firstItem) || !('value' in firstItem)) {
        errors.push('Pie/donut chart data items must have "name" and "value" properties');
      }
    } else if (d.chartType && !['pie', 'donut'].includes(d.chartType as string)) {
      if (!d.xKey && !d.yKey) {
        console.warn(`[validateChartData] widget "${widgetId}": chart should have xKey and yKey defined`);
      }
    }
  }

  if (errors.length > 0) {
    throw new WidgetValidationError('chart', errors, data);
  }

  return { valid: true, data };
};

/**
 * Validate list widget data shape - STRICT
 */
export const validateListData = (data: unknown, _widgetId = 'unknown'): ValidationResult => {
  const errors: string[] = [];

  if (data == null) {
    throw new WidgetValidationError('list', ['Data is null or undefined - endpoint may not exist'], data);
  }

  if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
    throw new WidgetValidationError('list', ['Received HTML instead of JSON - endpoint likely returns 404'], data);
  }

  const d = data as Record<string, unknown>;

  if (!Array.isArray(d.items)) {
    errors.push(`items must be an array, got ${typeof d.items}`);
  }

  if (Array.isArray(d.items) && d.items.length > 0) {
    const firstItem = d.items[0] as Record<string, unknown>;
    if (!firstItem.title || typeof firstItem.title !== 'string') {
      errors.push('List items must have a "title" string property');
    }
  }

  if (errors.length > 0) {
    throw new WidgetValidationError('list', errors, data);
  }

  return { valid: true, data };
};

/**
 * Validate API source data - catches endpoint failures early
 */
export const validateSourceData = (sourceData: unknown, dataSource: string): ValidationResult => {
  if (sourceData == null) {
    throw new WidgetValidationError('source', [`Data source "${dataSource}" returned null - API may be down`], sourceData);
  }

  if (typeof sourceData === 'string') {
    if (sourceData.includes('<!DOCTYPE') || sourceData.includes('<html')) {
      throw new WidgetValidationError(
        'source',
        [`Data source "${dataSource}" returned HTML - endpoint may not exist (404)`],
        sourceData.substring(0, 200)
      );
    }
  }

  const d = sourceData as Record<string, unknown>;
  if (d.error || d.detail || d.message) {
    const errorMsg = d.error || d.detail || d.message;
    throw new WidgetValidationError('source', [`Data source "${dataSource}" returned error: ${errorMsg}`], sourceData);
  }

  return { valid: true, data: sourceData };
};

/**
 * Get empty metric data fallback (used in catch blocks)
 */
export const getEmptyMetricData = (): MetricData => ({
  value: 0,
  label: 'Error',
  format: 'number',
  color: 'red',
  error: true,
});

/**
 * Get empty chart data fallback (used in catch blocks)
 */
export const getEmptyChartData = (): ChartData => ({
  chartType: 'bar',
  data: [],
  xKey: 'name',
  yKey: 'value',
  color: RED,
  error: true,
});

/**
 * Get empty list data fallback (used in catch blocks)
 */
export const getEmptyListData = (): ListData => ({
  items: [],
  maxItems: 0,
  showBadge: false,
  error: true,
});

/**
 * Auto-validate based on widget type - STRICT
 */
export const validateWidgetData = (widgetType: string, data: unknown, widgetId = 'unknown'): ValidationResult => {
  switch (widgetType) {
    case 'metric':
      return validateMetricData(data, widgetId);
    case 'chart':
      return validateChartData(data, widgetId);
    case 'list':
      return validateListData(data, widgetId);
    case 'custom':
      return { valid: true, data };
    default:
      return { valid: true, data };
  }
};

/**
 * Safe validation wrapper - catches errors and returns result object
 */
export const safeValidateWidgetData = (widgetType: string, data: unknown, widgetId = 'unknown'): ValidationResult => {
  try {
    return validateWidgetData(widgetType, data, widgetId);
  } catch (error: unknown) {
    if (error instanceof WidgetValidationError) {
      console.error(`Widget ${widgetId} validation failed:`, error.validationErrors);
      return {
        valid: false,
        data,
        errors: error.validationErrors,
      };
    }
    throw error;
  }
};

const widgetValidators = {
  validateMetricData,
  validateChartData,
  validateListData,
  validateSourceData,
  validateWidgetData,
  safeValidateWidgetData,
  getEmptyMetricData,
  getEmptyChartData,
  getEmptyListData,
  WidgetValidationError,
};

export { widgetValidators };
export default widgetValidators;
